'use client';

import { useEffect, useState } from 'react';

type Pdfjs = typeof import('pdfjs-dist/legacy/build/pdf.mjs');
type LoadingTask = ReturnType<Pdfjs['getDocument']>;
export type PdfDocument = Awaited<LoadingTask['promise']>;

/**
 * Bytes per range request: pages load on demand instead of the whole (up to 115 MB) catalogue.
 * Measured on the FERVI catalogues: opening takes ~0.5 MB, a page mostly 0.1–0.4 MB; 256 kB
 * chunks fetched several times more per page.
 */
const RANGE_CHUNK_SIZE = 64 * 1024;
const NO_IMAGES: ReadonlyMap<number, string> = new Map();

let pdfjsPromise: Promise<{ pdfjs: Pdfjs; worker: InstanceType<Pdfjs['PDFWorker']> }> | null = null;

/**
 * pdf.js and its worker load only when a flipbook needs them. One worker serves every PDF and is
 * passed to each document explicitly: with `GlobalWorkerOptions.workerPort` instead, destroying a
 * catalogue that is still loading tore down the worker the next catalogue was already using.
 */
function loadPdfjs() {
  pdfjsPromise ??= import('pdfjs-dist/legacy/build/pdf.mjs')
    .then(pdfjs => ({
      pdfjs,
      worker: pdfjs.PDFWorker.create({
        port: new Worker(new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url), {
          type: 'module',
        }),
      }),
    }))
    .catch(error => {
      // A failed chunk/worker must not poison every later catalogue opening.
      pdfjsPromise = null;
      throw error;
    });
  return pdfjsPromise;
}

type DocumentState = { url: string; doc: PdfDocument | null; failed: boolean };

const LOADING = { doc: null, failed: false } as const;

/** The PDF at `url` (null until it is needed); the state belongs to the url it was loaded for. */
export function usePdfDocument(url: string | null) {
  const [state, setState] = useState<DocumentState | null>(null);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    let task: LoadingTask | null = null;

    loadPdfjs()
      .then(({ pdfjs, worker }) => {
        if (cancelled) return null;
        task = pdfjs.getDocument({
          url,
          worker,
          disableAutoFetch: true,
          disableStream: true,
          rangeChunkSize: RANGE_CHUNK_SIZE,
          isEvalSupported: false,
        });
        return task.promise;
      })
      .then(doc => {
        if (!cancelled && doc) setState({ url, doc, failed: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url, doc: null, failed: true });
      });

    return () => {
      cancelled = true;
      void task?.destroy();
    };
  }, [url]);

  return state && state.url === url ? state : LOADING;
}

/** Renders a page to a JPEG object URL `width` pixels wide, on white like paper. */
async function renderPdfPage(doc: PdfDocument, pageNumber: number, width: number) {
  const page = await doc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);

  await page.render({ canvas, viewport, background: 'rgb(255,255,255)' }).promise;
  page.cleanup();

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', 0.86),
  );
  if (!blob) throw new Error(`Page ${pageNumber} could not be encoded.`);
  return URL.createObjectURL(blob);
}

type PageImages = {
  doc: PdfDocument;
  urls: ReadonlyMap<number, string>;
  request: (pageNumbers: number[]) => void;
  dispose: () => void;
};

/**
 * Page images of one document, rendered once each and one at a time. Every `request` replaces the
 * queue, so pages flipped or scrolled past are not rendered; with `limit`, older images are freed.
 */
function createPageImages(
  doc: PdfDocument,
  width: number,
  limit: number,
  onChange: () => void,
): PageImages {
  const urls = new Map<number, string>();
  let wanted: number[] = [];
  let queue: number[] = [];
  let running = false;
  let disposed = false;

  async function run() {
    running = true;
    for (let pageNumber = queue.shift(); pageNumber && !disposed; pageNumber = queue.shift()) {
      if (urls.has(pageNumber)) continue;
      try {
        const url = await renderPdfPage(doc, pageNumber, width);
        if (disposed) {
          URL.revokeObjectURL(url);
          break;
        }
        urls.set(pageNumber, url);
        release(urls, limit, wanted);
        onChange();
      } catch {
        // A page that fails keeps its placeholder; the link to the whole PDF stays available.
      }
    }
    running = false;
  }

  return {
    doc,
    urls,
    request(pageNumbers) {
      wanted = pageNumbers;
      queue = pageNumbers.filter(pageNumber => !urls.has(pageNumber));
      if (!running) void run();
    },
    dispose() {
      disposed = true;
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
    },
  };
}

function release(urls: Map<number, string>, limit: number, keep: number[]) {
  for (const [pageNumber, url] of urls) {
    if (urls.size <= limit) return;
    if (keep.includes(pageNumber)) continue;
    URL.revokeObjectURL(url);
    urls.delete(pageNumber);
  }
}

/**
 * Rendered pages of `doc` at `width` px. `request` renders the missing pages in the given order;
 * `limit` caps how many stay in memory (Infinity for thumbnails).
 */
export function usePdfPageImages(doc: PdfDocument | null, width: number, limit = Infinity) {
  const [images, setImages] = useState<PageImages | null>(null);
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!doc) return;

    const next = createPageImages(doc, width, limit, () => setVersion(version => version + 1));
    setImages(next);
    return () => next.dispose();
  }, [doc, width, limit]);

  const current = images?.doc === doc ? images : null;
  return { urls: current?.urls ?? NO_IMAGES, request: current?.request };
}
